import { ShieldCheck, ShieldAlert, AlertTriangle, Circle, Calendar } from 'lucide-react';

export type StatusFilter = 's' | 'e-open' | 'open' | 'schedule';

interface Props {
  // Данные авто-анализа (readiness-summary), а не замечаний:
  staleDecisions: number | null;   // решения на устаревших обоснованиях; null = не проверено
  completenessGaps: number | null; // пробелы состава ПД; null = не проверено
  openCount: number;               // открытых замечаний (из remarks-dashboard)
  deadline?: string | null;        // срок сдачи (ISO), null/пусто = не задан
  onChipClick: (kind: StatusFilter) => void;
  lastUpdated?: string;
  onRefresh?: () => void;
  // IA заказчика (Этап 2): тонкая строка — только «Открытых» + обновление.
  // Чипы Решение/Комплектность дублируют карточки «Готовность» ниже (у заказчика
  // они прямо под полосой), Срок дублирует инлайн в шапке → убираем. Статусы
  // читаются на карточках, не здесь. Для остальных ролей полоса как была.
  slim?: boolean;
}

/** Status-bar: Решение / Комплектность (данные движка, инфо) + Открытых / Срок (кликабельны). */
export function StatusBar({ staleDecisions, completenessGaps, openCount, deadline, onChipClick, lastUpdated = '—', onRefresh, slim = false }: Props) {
  // Срок: показываем конкретную дату + остаток дней, а не бинарное «задан».
  const dl = deadline ? new Date(deadline) : null;
  const dlValid = dl && !isNaN(dl.getTime());
  const daysLeft = dlValid ? Math.ceil((dl!.getTime() - Date.now()) / 86_400_000) : null;
  const dlText = !dlValid
    ? 'Срок: не задан'
    : daysLeft! < 0
      ? `Срок: просрочен (${dl!.toLocaleDateString('ru-RU')})`
      : `Срок: ${dl!.toLocaleDateString('ru-RU')} · ${daysLeft} дн.`;
  // Решение: сколько проектных решений опираются на устаревшие обоснования.
  const decOk = staleDecisions === 0;
  const decUnknown = staleDecisions == null;
  const decColor = decUnknown ? '#6B7280' : decOk ? '#10B981' : '#EF4444';
  const decText = decUnknown ? 'Решение: не проверено'
    : decOk ? 'Решение: без устаревших'
    : `Решение: на устаревших ${staleDecisions} обоснованиях`;
  // Комплектность: пробелы состава документации по ПП-87 (находки движка).
  const cmpOk = completenessGaps === 0;
  const cmpUnknown = completenessGaps == null;
  const cmpColor = cmpUnknown ? '#6B7280' : cmpOk ? '#10B981' : '#EF4444';
  const cmpText = cmpUnknown ? 'Комплектность: не проверена'
    : `Комплектность: ${completenessGaps} ${plural(completenessGaps!, 'пробел', 'пробела', 'пробелов')}`;
  const tint = (hex: string, a: number) => hex === '#6B7280' ? '#F3F4F6' : `${hex}${Math.round(a * 255).toString(16).padStart(2, '0')}`;
  return (
    <div className="bg-white border-b border-border" style={{ padding: '16px 0' }}>
      <div className="mx-auto flex items-center" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, gap: 12, flexWrap: 'wrap' }}>
        {/* Заказчику (slim) — только «Открытых замечаний»: проверки живут карточками
            «Готовность» ниже, срок — инлайн в шапке. Остальным ролям — полный набор. */}
        {!slim && (
          <Chip
            icon={decOk ? ShieldCheck : ShieldAlert}
            iconColor={decColor}
            text={decText}
            bg={tint(decColor, 0.1)}
            border={tint(decColor, 0.3)}
            textColor={decColor}
            fontWeight={600}
          />
        )}
        {!slim && (
          <Chip
            icon={cmpOk ? ShieldCheck : AlertTriangle}
            iconColor={cmpColor}
            text={cmpText}
            bg={tint(cmpColor, 0.1)}
            border={tint(cmpColor, 0.3)}
            textColor={cmpColor}
          />
        )}
        <Chip
          icon={Circle}
          iconColor={openCount > 0 ? '#EF4444' : '#6B7280'}
          text={slim ? `Открытых замечаний: ${openCount}` : `Открытых: ${openCount}`}
          bg={openCount > 0 ? 'rgba(239,68,68,.1)' : '#F3F4F6'}
          border={openCount > 0 ? 'rgba(239,68,68,.2)' : '#E5E7EB'}
          onClick={() => onChipClick('open')}
        />
        {!slim && (
          <Chip
            icon={Calendar}
            iconColor={dlValid ? '#0A1628' : '#6B7280'}
            text={dlText}
            bg="#F3F4F6"
            border="#E5E7EB"
            textColor={dlValid ? '#0A1628' : '#6B7280'}
            onClick={() => onChipClick('schedule')}
          />
        )}
        <div className="flex-1" />
        <span className="text-muted tabular" style={{ fontSize: 12 }}>Последнее обновление: {lastUpdated}</span>
        {onRefresh && (
          <button type="button" onClick={onRefresh} className="text-amber-hover" style={{ fontSize: 12, background: 'none', border: 'none', cursor: 'pointer' }}>
            обновить
          </button>
        )}
      </div>
    </div>
  );
}

interface ChipProps {
  icon: typeof Circle;
  iconColor: string;
  text: string;
  bg: string;
  border: string;
  textColor?: string;
  fontWeight?: number;
  onClick?: () => void;   // без onClick — чип чисто информационный (не кнопка)
}
function Chip({ icon: Icon, iconColor, text, bg, border, textColor = '#0A1628', fontWeight = 500, onClick }: ChipProps) {
  const style = {
    padding: '6px 12px', gap: 8,
    background: bg, border: `1px solid ${border}`,
    color: textColor, fontWeight, fontSize: 13,
    cursor: onClick ? 'pointer' : 'default',
  } as const;
  const inner = (<><Icon size={16} stroke={iconColor} /><span>{text}</span></>);
  if (!onClick) {
    return <div className="inline-flex items-center rounded-pill" style={style}>{inner}</div>;
  }
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center rounded-pill hover:opacity-90" style={style}>
      {inner}
    </button>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
